import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useOrganization } from '@/contexts/OrganizationContext';
import type { ContentPlatform } from '@/types/performanceMetrics';
import type { PlatformDistributionItem } from '../components/charts/PlatformDistributionChart';

const PLATFORM_CONFIG: Record<ContentPlatform, { label: string; color: string }> = {
  instagram: { label: 'Instagram', color: '#E91E63' },
  youtube:   { label: 'YouTube',   color: '#FF0000' },
  tiktok:    { label: 'TikTok',    color: '#00F2EA' },
  twitter:   { label: 'Twitter',   color: '#1DA1F2' },
  linkedin:  { label: 'LinkedIn',  color: '#0A66C2' },
};

interface RpcPlatformRow {
  platform: string;
  pct: number;
}

export function usePlatformDistribution(campaignId: string) {
  const { currentOrganization } = useOrganization();

  return useQuery<PlatformDistributionItem[]>({
    queryKey: ['platform-distribution', campaignId, currentOrganization?.id],
    queryFn: async (): Promise<PlatformDistributionItem[]> => {
      const { data, error } = await supabase.rpc('get_platform_distribution', {
        p_campaign_id: campaignId,
      });

      if (error) throw error;

      const rows = (data as unknown as RpcPlatformRow[] | null) ?? [];

      return rows
        .map((row): PlatformDistributionItem | null => {
          const config = PLATFORM_CONFIG[row.platform as ContentPlatform];
          if (!config) return null;
          return {
            name: config.label,
            value: row.pct,
            color: config.color,
          };
        })
        .filter((item): item is PlatformDistributionItem => item !== null);
    },
    enabled: !!campaignId && !!currentOrganization?.id,
    staleTime: 3 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}
